import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  X,
  Package,
  Info,
  StickyNote,
  RotateCcw,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Check,
  ShieldAlert,
  ShieldCheck,
  Search,
} from "lucide-react";
import { domain } from "../../../../security";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const QtyInput = ({ value, onChange, colorClass, max }) => (
  <div className="relative w-20 mx-auto">
    <input
      type="number"
      min="0"
      max={max}
      value={value === undefined || value === "" ? "" : value}
      onChange={onChange}
      placeholder="0"
      className={`w-full block text-center py-2 border rounded-xl text-sm font-black outline-none focus:ring-2 transition shadow-inner bg-opacity-50 ${colorClass}`}
    />
  </div>
);

const StepIndicator = ({ step }) => {
  const steps = [
    { n: 1, label: "Condition" },
    { n: 2, label: "Action" },
    { n: 3, label: "Review" },
  ];
  return (
    <div className="flex items-center justify-center py-4 bg-slate-50 border-b border-slate-100">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === s.n ? "bg-emerald-600 text-white shadow-md shadow-emerald-200" : step > s.n ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"}`}
            >
              {step > s.n ? <CheckCircle size={14} /> : s.n}
            </div>
            <span
              className={`text-xs font-bold tracking-wide uppercase ${step === s.n ? "text-emerald-700" : step > s.n ? "text-emerald-600" : "text-slate-400"}`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`w-12 sm:w-20 h-0.5 mx-2 sm:mx-4 rounded-full ${step > s.n ? "bg-emerald-300" : "bg-slate-200"}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── SERIAL SELECTION MODAL ──────────────────
const SerialSelectionModal = ({
  isOpen,
  onClose,
  item,
  locationId,
  initialSelections,
  onSave,
}) => {
  const [serials, setSerials] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (isOpen && item) {
      const map = {};
      (initialSelections?.goodSerialIds || []).forEach(
        (id) => (map[id] = "GOOD"),
      );
      (initialSelections?.badSerialIds || []).forEach(
        (id) => (map[id] = "BAD"),
      );
      setSelections(map);

      const fetchSerials = async () => {
        setLoading(true);
        try {
          const res = await axios.get(
            `${domain}/api/TradeReturns/sold-serials/${item.productId}?locationId=${locationId || ""}`,
          );
          setSerials(res.data);
        } catch {
          toast.error("Failed to load sold serials.");
        } finally {
          setLoading(false);
        }
      };
      fetchSerials();
    }
  }, [isOpen, item, locationId, initialSelections]);

  const filteredSerials = useMemo(
    () =>
      serials.filter((s) =>
        (s.serialName || "").toLowerCase().includes(search.toLowerCase()),
      ),
    [serials, search],
  );

  const toggleSerial = (id) => {
    setSelections((prev) => {
      const current = prev[id];
      if (!current) return { ...prev, [id]: "GOOD" };
      if (current === "GOOD") return { ...prev, [id]: "BAD" };
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = () => {
    const goodIds = Object.keys(selections)
      .filter((k) => selections[k] === "GOOD")
      .map(Number);
    const badIds = Object.keys(selections)
      .filter((k) => selections[k] === "BAD")
      .map(Number);
    onSave(item.productId, goodIds, badIds);
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-slate-50">
          <div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="text-emerald-500" size={18} /> Select
              Serials
            </h2>
            <p className="text-xs text-slate-500">{item.productName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 border-b">
          <Search className="absolute left-7 top-[78px] h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search Serial..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 py-2 border rounded-xl text-sm"
          />
        </div>
        <div className="p-5 flex-1 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="text-center py-10 text-slate-400">
              Loading serials...
            </div>
          ) : filteredSerials.length === 0 ? (
            <div className="text-center py-10 text-slate-400 italic">
              No sold serial numbers found.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {filteredSerials.map((s) => {
                const status = selections[s.serialNumberId];
                let btnCls = "bg-white border-slate-300 text-slate-600";
                if (status === "GOOD")
                  btnCls =
                    "bg-emerald-50 border-emerald-400 text-emerald-700 ring-1";
                if (status === "BAD")
                  btnCls =
                    "bg-orange-50 border-orange-400 text-orange-700 ring-1";
                return (
                  <button
                    key={s.serialNumberId}
                    onClick={() => toggleSerial(s.serialNumberId)}
                    className={`px-3 py-2 text-xs font-mono font-bold border rounded-lg shadow-sm ${btnCls}`}
                  >
                    {s.serialName}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-xl"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 text-sm font-bold bg-slate-800 text-white rounded-xl hover:bg-slate-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────
export default function ProcessTradeReturnModal({
  returnRecord,
  onClose,
  onSuccess,
  currentUserId,
}) {
  const [step, setStep] = useState(1);
  const items = useMemo(() => returnRecord?.items || [], [returnRecord]);
  const [splits, setSplits] = useState({});
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeSerialItem, setActiveSerialItem] = useState(null);

  useEffect(() => {
    if (items.length === 0) return;
    const init = {};
    items.forEach((item) => {
      init[item.productId] = {
        good: 0,
        damaged: 0,
        cashGood: 0,
        replaceGood: 0,
        cashDamaged: 0,
        replaceDamaged: 0,
        goodSerialIds: [],
        badSerialIds: [],
      };
    });
    setSplits(init);
  }, [items]);

  const handleConditionChange = (productId, field, rawVal, maxQty) => {
    let val = rawVal === "" ? "" : parseInt(rawVal);
    if (val < 0) val = 0;
    setSplits((prev) => {
      const curr = { ...(prev[productId] || {}) };
      const otherVal =
        parseInt(curr[field === "good" ? "damaged" : "good"]) || 0;
      if (typeof val === "number" && val + otherVal > maxQty)
        val = maxQty - otherVal;
      curr[field] = val;
      curr.cashGood = 0;
      curr.replaceGood = 0;
      curr.cashDamaged = 0;
      curr.replaceDamaged = 0;
      return { ...prev, [productId]: curr };
    });
  };

  const handleSerialSave = (productId, goodIds, badIds) => {
    setSplits((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        goodSerialIds: goodIds,
        badSerialIds: badIds,
        good: goodIds.length,
        damaged: badIds.length,
        cashGood: 0,
        replaceGood: 0,
        cashDamaged: 0,
        replaceDamaged: 0,
      },
    }));
    setActiveSerialItem(null);
  };

  const handleActionChange = (productId, pool, field, rawVal, maxPoolQty) => {
    let val = rawVal === "" ? "" : parseInt(rawVal);
    if (val < 0) val = 0;
    setSplits((prev) => {
      const curr = { ...(prev[productId] || {}) };
      const otherField =
        pool === "good"
          ? field === "cashGood"
            ? "replaceGood"
            : "cashGood"
          : field === "cashDamaged"
            ? "replaceDamaged"
            : "cashDamaged";
      const otherVal = parseInt(curr[otherField]) || 0;
      if (typeof val === "number" && val + otherVal > maxPoolQty)
        val = maxPoolQty - otherVal;
      curr[field] = val;
      return { ...prev, [productId]: curr };
    });
  };

  const canProceedStep1 = useMemo(
    () =>
      items.every((item) => {
        const s = splits[item.productId] || {};
        return (
          (parseInt(s.good) || 0) + (parseInt(s.damaged) || 0) ===
          item.quantityReturned
        );
      }),
    [items, splits],
  );
  const canProceedStep2 = useMemo(
    () =>
      items.every((item) => {
        const s = splits[item.productId] || {};
        return (
          (parseInt(s.cashGood) || 0) + (parseInt(s.replaceGood) || 0) ===
            (parseInt(s.good) || 0) &&
          (parseInt(s.cashDamaged) || 0) + (parseInt(s.replaceDamaged) || 0) ===
            (parseInt(s.damaged) || 0)
        );
      }),
    [items, splits],
  );

  // ✅ Here is where Unit Price makes the total perfect!
  const totalRefund = useMemo(
    () =>
      items.reduce((sum, item) => {
        const s = splits[item.productId] || {};
        const totalCash =
          (parseInt(s.cashGood) || 0) + (parseInt(s.cashDamaged) || 0);
        return sum + totalCash * (item.unitPrice || 0);
      }, 0),
    [splits, items],
  );

  const handleSubmit = async () => {
    const payload = {
      UserID: currentUserId,
      Note: note,
      Items: items.map((item) => {
        const s = splits[item.productId] || {};
        return {
          ProductId: item.productId,
          CashQty: (parseInt(s.cashGood) || 0) + (parseInt(s.cashDamaged) || 0),
          ReplaceQty:
            (parseInt(s.replaceGood) || 0) + (parseInt(s.replaceDamaged) || 0),
          QuantityGood: parseInt(s.good) || 0,
          QuantityBad: parseInt(s.damaged) || 0,
          GoodSerialNumberIds: s.goodSerialIds || [],
          BadSerialNumberIds: s.badSerialIds || [],
        };
      }),
    };
    setIsLoading(true);
    try {
      await axios.put(
        `${domain}/api/TradeReturns/${returnRecord.id}/process`,
        payload,
      );
      toast.success("Trade Return processed.");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to process.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
        <div className="relative flex h-full max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="shrink-0 bg-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-600 rounded-xl text-white shadow-md shadow-emerald-200">
                <Package size={18} />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900 leading-tight">
                  Process Trade Return
                </h2>
                <p className="text-xs font-semibold text-slate-500">
                  {returnRecord?.returnNumber} ·{" "}
                  <span className="text-emerald-600">
                    {returnRecord?.customerName}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          <StepIndicator step={step} />

          <div className="flex-1 overflow-y-auto bg-slate-50/50 p-6">
            {step === 1 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b text-[10px] font-black uppercase">
                      <tr>
                        <th className="p-4 w-[40%]">Product</th>
                        <th className="p-4 text-center">Total Returned</th>
                        <th className="p-4 text-center text-emerald-600">
                          Good Qty
                        </th>
                        <th className="p-4 text-center text-orange-600">
                          Damaged Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item) => {
                        const s = splits[item.productId] || {};
                        const missing =
                          item.quantityReturned -
                          ((parseInt(s.good) || 0) +
                            (parseInt(s.damaged) || 0));
                        return (
                          <tr key={item.productId}>
                            <td className="p-4">
                              <p className="font-bold text-slate-800">
                                {item.productName}
                              </p>
                              {item.hasSerial && (
                                <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold tracking-wider inline-block mt-1">
                                  SERIALIZED
                                </span>
                              )}
                              <p className="text-[10px] font-bold text-slate-400 mt-0.5">
                                {item.salesUomName}
                              </p>
                            </td>
                            <td className="p-4 text-center bg-slate-50/30 border-x border-slate-100">
                              <span className="text-lg font-black text-slate-700">
                                {item.quantityReturned}
                              </span>
                              {missing > 0 ? (
                                <p className="text-[10px] font-bold text-amber-500 mt-1 animate-pulse">
                                  {missing} missing
                                </p>
                              ) : (
                                <p className="flex items-center justify-center gap-1 text-[10px] font-bold text-emerald-500 mt-1">
                                  <Check size={12} /> Balanced
                                </p>
                              )}
                            </td>
                            {item.hasSerial ? (
                              <td colSpan={2} className="p-4 bg-slate-50/50">
                                <div className="flex items-center justify-between gap-3 px-2">
                                  <div className="text-xs font-bold flex gap-4">
                                    <span className="text-emerald-600">
                                      {s.good || 0} Good
                                    </span>
                                    <span className="text-orange-600">
                                      {s.damaged || 0} Damaged
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setActiveSerialItem(item)}
                                    className="px-4 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 flex gap-2"
                                  >
                                    <ShieldCheck size={14} /> Select Serials
                                  </button>
                                </div>
                              </td>
                            ) : (
                              <>
                                <td className="p-4">
                                  <QtyInput
                                    value={s.good}
                                    onChange={(e) =>
                                      handleConditionChange(
                                        item.productId,
                                        "good",
                                        e.target.value,
                                        item.quantityReturned,
                                      )
                                    }
                                    max={item.quantityReturned}
                                    colorClass="border-emerald-200 text-emerald-700 focus:border-emerald-400 bg-emerald-50/30"
                                  />
                                </td>
                                <td className="p-4">
                                  <QtyInput
                                    value={s.damaged}
                                    onChange={(e) =>
                                      handleConditionChange(
                                        item.productId,
                                        "damaged",
                                        e.target.value,
                                        item.quantityReturned,
                                      )
                                    }
                                    max={item.quantityReturned}
                                    colorClass="border-orange-200 text-orange-700 focus:border-orange-400 bg-orange-50/30"
                                  />
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b text-[10px] font-black uppercase">
                      <tr>
                        <th className="p-4 w-[45%]">Item Pool</th>
                        <th className="p-4 text-center">Qty to Process</th>
                        <th className="p-4 text-center text-red-600">
                          Cash Refund
                        </th>
                        <th className="p-4 text-center text-blue-600">
                          Replace Item
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item) => {
                        const s = splits[item.productId] || {};
                        const goodQty = parseInt(s.good) || 0;
                        const damagedQty = parseInt(s.damaged) || 0;
                        return (
                          <React.Fragment key={item.productId}>
                            {goodQty > 0 && (
                              <tr>
                                <td className="p-4">
                                  <p className="font-bold text-slate-800">
                                    {item.productName}
                                  </p>
                                  <span className="text-[10px] font-bold text-emerald-600">
                                    GOOD CONDITION
                                  </span>
                                </td>
                                <td className="p-4 text-center">
                                  <span className="text-base font-black text-emerald-700">
                                    {goodQty}
                                  </span>
                                </td>
                                <td className="p-4 bg-red-50/10">
                                  <QtyInput
                                    value={s.cashGood}
                                    onChange={(e) =>
                                      handleActionChange(
                                        item.productId,
                                        "good",
                                        "cashGood",
                                        e.target.value,
                                        goodQty,
                                      )
                                    }
                                    max={goodQty}
                                    colorClass="border-red-200 text-red-700"
                                  />
                                </td>
                                <td className="p-4 bg-blue-50/10">
                                  <QtyInput
                                    value={s.replaceGood}
                                    onChange={(e) =>
                                      handleActionChange(
                                        item.productId,
                                        "good",
                                        "replaceGood",
                                        e.target.value,
                                        goodQty,
                                      )
                                    }
                                    max={goodQty}
                                    colorClass="border-blue-200 text-blue-700"
                                  />
                                </td>
                              </tr>
                            )}
                            {damagedQty > 0 && (
                              <tr>
                                <td className="p-4">
                                  <p className="font-bold text-slate-800">
                                    {item.productName}
                                  </p>
                                  <span className="text-[10px] font-bold text-orange-600">
                                    DAMAGED
                                  </span>
                                </td>
                                <td className="p-4 text-center">
                                  <span className="text-base font-black text-orange-700">
                                    {damagedQty}
                                  </span>
                                </td>
                                <td className="p-4 bg-red-50/10">
                                  <QtyInput
                                    value={s.cashDamaged}
                                    onChange={(e) =>
                                      handleActionChange(
                                        item.productId,
                                        "damaged",
                                        "cashDamaged",
                                        e.target.value,
                                        damagedQty,
                                      )
                                    }
                                    max={damagedQty}
                                    colorClass="border-red-200 text-red-700"
                                  />
                                </td>
                                <td className="p-4 bg-blue-50/10">
                                  <QtyInput
                                    value={s.replaceDamaged}
                                    onChange={(e) =>
                                      handleActionChange(
                                        item.productId,
                                        "damaged",
                                        "replaceDamaged",
                                        e.target.value,
                                        damagedQty,
                                      )
                                    }
                                    max={damagedQty}
                                    colorClass="border-blue-200 text-blue-700"
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 bg-white border rounded-xl p-5 shadow-sm space-y-4">
                    <h3 className="text-[11px] font-black uppercase text-slate-400 tracking-widest border-b pb-2">
                      Action Summary
                    </h3>
                    <div className="space-y-3">
                      {items.map((item) => {
                        const s = splits[item.productId] || {};
                        const totalCash =
                          (parseInt(s.cashGood) || 0) +
                          (parseInt(s.cashDamaged) || 0);
                        const totalReplace =
                          (parseInt(s.replaceGood) || 0) +
                          (parseInt(s.replaceDamaged) || 0);
                        return (
                          <div
                            key={item.productId}
                            className="flex justify-between p-3 bg-slate-50 border rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-bold text-slate-800">
                                {item.productName}
                              </p>
                              <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                                @ {formatCurrency(item.unitPrice)} ea.
                              </p>
                            </div>
                            <div className="flex gap-4 text-xs font-bold text-right">
                              {totalCash > 0 && (
                                <div className="text-red-600 flex flex-col">
                                  <span>{totalCash} Cash Refund</span>
                                  <span className="text-[9px]">
                                    (
                                    {formatCurrency(totalCash * item.unitPrice)}
                                    )
                                  </span>
                                </div>
                              )}
                              {totalReplace > 0 && (
                                <div className="text-blue-600 flex flex-col justify-center">
                                  <span>{totalReplace} Replace</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-100 rounded-xl p-5 shadow-sm flex flex-col items-center justify-center text-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-1">
                        Total Cash Refund
                      </p>
                      <p className="text-3xl font-black text-red-600">
                        {formatCurrency(totalRefund)}
                      </p>
                    </div>
                    <div className="bg-white border rounded-xl p-4 shadow-sm">
                      <label className="flex items-center gap-1.5 text-[11px] font-black uppercase text-slate-500 mb-2">
                        <StickyNote size={13} /> Processing Note
                      </label>
                      <textarea
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note..."
                        className="block w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-200 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t bg-white px-6 py-4 flex justify-between gap-4">
            <div>
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200"
                >
                  <ArrowLeft size={16} /> Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={
                    (step === 1 && !canProceedStep1) ||
                    (step === 2 && !canProceedStep2)
                  }
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                >
                  Next Step <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}{" "}
                  Confirm & Process
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <SerialSelectionModal
        isOpen={!!activeSerialItem}
        onClose={() => setActiveSerialItem(null)}
        item={activeSerialItem}
        locationId={returnRecord?.locationId}
        initialSelections={splits[activeSerialItem?.productId]}
        onSave={handleSerialSave}
      />
    </>
  );
}
